import { AUDIENCE_EXPORT_TYPES, ENUM_REGEX } from '../../../constants/index';
import { MarginOutlined } from '@mui/icons-material';

import {
  AMAZON_SEGMENT_FIELD_NAME,
  APPSFLYER_SEGMENT_FIELD_NAME,
  VIANT_SEGMENT_FIELD_NAME,
  LIVERAMP_SEGMENT_FIELD_NAME,
  META_MODEL_CUT_SIZE,
  META_SEGMENT_FIELD_NAME,
  NEXXEN_AVAILABILITY_ALL,
  NEXXEN_AVAILABILITY_ALL_LABEL,
  NEXXEN_AVAILABILITY_SINGLE,
  NEXXEN_AVAILABILITY_SINGLE_LABEL,
  NEXXEN_RATE_TYPE_CPMAP,
  NEXXEN_RATE_TYPE_CPMAP_LABEL,
  NEXXEN_RATE_TYPE_CPM,
  NEXXEN_RATE_TYPE_CPM_LABEL,
  NEXXEN_RATE_TYPE_FF,
  NEXXEN_RATE_TYPE_FF_LABEL,
  NEXXEN_RATE_TYPE_PM,
  NEXXEN_RATE_TYPE_PM_LABEL,
  NEXXEN_SEGMENT_FIELD_NAME,
  REDDIT_SEGMENT_FIELD_NAME,
  TIKTOK_AUDIENCE_SIZE_BALANCED,
  TIKTOK_AUDIENCE_SIZE_BROAD,
  TIKTOK_AUDIENCE_SIZE_NARROW,
  TIKTOK_MOBILE_OS_ALL,
  TIKTOK_MOBILE_OS_ANDROID,
  TIKTOK_MOBILE_OS_IOS,
  TIKTOK_PLACEMENTS_TOOLTIP,
  TIKTOK_PLACEMENT_GLOBAL_APP_BUNDLE,
  TIKTOK_PLACEMENT_TIKTOK,
  TIKTOK_SEGMENT_FIELD_NAME,
  TRADE_DESK_SEGMENT_FIELD_NAME,
  YAHOO_DSP_SEGMENT_FIELD_NAME,
} from '../../../content';
import { nanoid } from 'nanoid';

/**
 * Configuration for Trade Desk Custom Segment fields.
 */
const tradeDeskCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'partnerID',
    column: 'partnerID',
    label: TRADE_DESK_SEGMENT_FIELD_NAME.PARTNER_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
  {
    type: 'label',
    label: 'Or',
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'textbox',
    id: 'advertiserID',
    column: 'advertiserID',
    label: TRADE_DESK_SEGMENT_FIELD_NAME.ADVERTISER_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for LiveRamp Custom Segment fields.
 */
const liveRampCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'tvTargeting',
    column: 'tvTargeting',
    label: LIVERAMP_SEGMENT_FIELD_NAME.TV_TARGETING,
    regex: ENUM_REGEX.DECIMAL, // Only decimal values are allowed.
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
  {
    id: 'space', // Placeholder for spacing between fields.
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'textbox',
    id: 'advertiserDirect',
    column: 'advertiserDirect',
    required: false,
    label: LIVERAMP_SEGMENT_FIELD_NAME.ADVERTISER_DIRECT,
    regex: ENUM_REGEX.WHOLE_NUMBER,
    variant: 'outlined',
    gridProps: { xs: 5 },
    value: '',
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
  {
    type: 'autocomplete',
    id: 'platformDestination',
    column: 'platformDestination',
    placeholder: LIVERAMP_SEGMENT_FIELD_NAME.CHOOSE_INTEGRATION,
    variant: 'outlined',
    options: [],
    required: false,
    gridProps: { xs: 12 },
    value: '',
    isVisible: true,
  },
  {
    type: 'autocomplete',
    id: 'advertiserDestination',
    column: 'advertiserDestination',
    placeholder: LIVERAMP_SEGMENT_FIELD_NAME.CHOOSE_ADVERTISER,
    variant: 'outlined',
    options: [],
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: true,
    isVisible: true,
  },
];

/**
 * Configuration for LiveRamp OTS Segment fields.
 */
const liveRampOtsSegmentConfig = [
  {
    type: 'textbox',
    id: 'tvTargeting',
    column: 'tvTargeting',
    label: LIVERAMP_SEGMENT_FIELD_NAME.TV_TARGETING,
    regex: ENUM_REGEX.DECIMAL, // Only decimal values are allowed.
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
  {
    id: 'space', // Placeholder for spacing between fields.
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'textbox',
    id: 'advertiserDirect',
    column: 'advertiserDirect',
    label: LIVERAMP_SEGMENT_FIELD_NAME.ADVERTISER_DIRECT,
    regex: ENUM_REGEX.WHOLE_NUMBER,
    variant: 'outlined',
    gridProps: { xs: 5 },
    value: '',
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for Trade Desk OTS Segment fields.
 */
const tradeDeskOTSSegmentConfig: any[] = [];

/**
 * Configuration for Meta Custom Segment Seed Audience fields.
 */
const metaCustomSegmentSeedAudienceConfig = [
  {
    type: 'textbox',
    id: 'metaAdAccountId',
    column: 'metaAdAccountId',
    label: META_SEGMENT_FIELD_NAME.META_AD_ACCOUNT_ID,
    regex: ENUM_REGEX.WHOLE_NUMBER, // Only whole numbers are allowed for Meta Ad Account ID
    helperText: 'Digits only, no "act_"',
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
  },
  {
    type: 'dropdown',
    id: 'metaLalRatio',
    column: 'metaLalRatio',
    label: META_SEGMENT_FIELD_NAME.MODEL_CUT_SIZE,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 3 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    style: { marginTop: 5 },
    options: [
      { value: 1, label: '1' },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
      { value: 4, label: '4' },
      { value: 5, label: '5' },
      { value: 6, label: '6' },
      { value: 7, label: '7' },
      { value: 8, label: '8' },
      { value: 9, label: '9' },
      { value: 10, label: '10' },
    ],
  },
  {
    type: 'label',
    id: 'metaLalRatioLabel',
    label: '',
    isVisible: true,
    gridProps: { xs: 6 },
    style: {
      marginTop: 22,
      marginLeft: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
    },
  },
];

/**
 * Configuration for Meta Custom Segment Modeled Audience fields.
 * Note: This does NOT include metaLalRatio (Model Cut Size) field.
 */
const metaCustomSegmentModeledAudienceConfig = [
  {
    type: 'textbox',
    id: 'metaAdAccountId',
    column: 'metaAdAccountId',
    label: META_SEGMENT_FIELD_NAME.META_AD_ACCOUNT_ID,
    regex: ENUM_REGEX.WHOLE_NUMBER, // Only whole numbers are allowed for Meta Ad Account ID
    helperText: 'Digits only, no "act_"',
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for Yahoo DSP OTS Segment fields.
 */
const yahooDspOTSSegmentConfig = [
  {
    type: 'textbox',
    id: 'tvCpm',
    column: 'tvCpm',
    label: YAHOO_DSP_SEGMENT_FIELD_NAME.TV_CPM,
    regex: ENUM_REGEX.DECIMAL, // Only decimal values are allowed.
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for Yahoo DSP Custom Segment fields.
 */
const yahooDspCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'partnerID',
    column: 'partnerID',
    label: YAHOO_DSP_SEGMENT_FIELD_NAME.PARTNER_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
  {
    type: 'label',
    label: '',
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'textbox',
    id: 'tvCpm',
    column: 'tvCpm',
    label: YAHOO_DSP_SEGMENT_FIELD_NAME.TV_CPM,
    regex: ENUM_REGEX.DECIMAL,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for TikTok Custom Segment — seed audience only (Mobile OS + Audience Size).
 */
const tiktokCustomSegmentSeedAudienceConfig = [
  {
    type: 'textbox',
    id: 'advertiserID',
    column: 'advertiserID',
    label: TIKTOK_SEGMENT_FIELD_NAME.ADVERTISER_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
  },
  {
    type: 'dropdown',
    id: 'mobileOs',
    column: 'mobileOs',
    label: TIKTOK_SEGMENT_FIELD_NAME.MOBILE_OS,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 6 },
    style: { marginTop: 5, marginRight: '8px' },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    options: [
      { value: '0', label: TIKTOK_MOBILE_OS_ALL },
      { value: '1', label: TIKTOK_MOBILE_OS_ANDROID },
      { value: '2', label: TIKTOK_MOBILE_OS_IOS },
    ],
  },
  {
    type: 'dropdown',
    id: 'audienceModelingBreadth',
    column: 'audienceModelingBreadth',
    label: TIKTOK_SEGMENT_FIELD_NAME.AUDIENCE_SIZE,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 6 },
    style: { marginTop: 5, marginLeft: '8px', marginRight: '8px' },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    options: [
      { value: 'modeling_breadth_narrow', label: TIKTOK_AUDIENCE_SIZE_NARROW },
      {
        value: 'modeling_breadth_balanced',
        label: TIKTOK_AUDIENCE_SIZE_BALANCED,
      },
      { value: 'modeling_breadth_broad', label: TIKTOK_AUDIENCE_SIZE_BROAD },
    ],
  },
  {
    type: 'multiselect',
    id: 'placements',
    column: 'placements',
    label: TIKTOK_SEGMENT_FIELD_NAME.PLACEMENTS,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 6, style: { display: 'none' } },
    value: ['tiktok_placement_tiktok'],
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    options: [
      { value: 'tiktok_placement_tiktok', label: TIKTOK_PLACEMENT_TIKTOK },
      {
        value: 'tiktok_placement_global_app_bundle',
        label: TIKTOK_PLACEMENT_GLOBAL_APP_BUNDLE,
      },
    ],
    tooltip: TIKTOK_PLACEMENTS_TOOLTIP,
  },
];

/**
 * Configuration for TikTok Custom Segment — modeled (lookalike) audience.
 * Targeting (placements, mobile OS, audience size, etc.) is seed-only; modeled exports omit it downstream.
 */
const tiktokCustomSegmentModeledAudienceConfig = [
  {
    type: 'textbox',
    id: 'advertiserID',
    column: 'advertiserID',
    label: TIKTOK_SEGMENT_FIELD_NAME.ADVERTISER_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for Reddit Custom Segment fields.
 */
const redditCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'platformDestination',
    column: 'platformDestination',
    label: REDDIT_SEGMENT_FIELD_NAME.REDDIT_AD_ACCOUNT_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for AppsFlyer Custom Segment fields (modeled audience).
 */
const appsflyerCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'advertiserID',
    column: 'advertiserID',
    label: APPSFLYER_SEGMENT_FIELD_NAME.COLLABORATOR_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for Viant Custom Segment fields (modeled audience).
 */
const viantCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'accountID',
    column: 'accountID',
    label: VIANT_SEGMENT_FIELD_NAME.ACCOUNT_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
    spacing: 'md',
  },
  {
    type: 'textbox',
    id: 'advertiserID',
    column: 'advertiserID',
    label: VIANT_SEGMENT_FIELD_NAME.ADVERTISER_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
    spacing: 'md',
  },
];

/**
 * Configuration for Amazon Custom Segment fields.
 */
const amazonCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'advertiserID',
    column: 'advertiserID',
    label: AMAZON_SEGMENT_FIELD_NAME.ADVERTISER_ID,
    variant: 'outlined',
    required: true,
    regex: ENUM_REGEX.WHOLE_NUMBER,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for Nexxen OTS Segment fields.
 */
const nexxenOTSSegmentConfig = [
  {
    type: 'dropdown',
    id: 'rateType',
    column: 'rateType',
    label: NEXXEN_SEGMENT_FIELD_NAME.RATE_TYPE,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    spacing: 'lg',
    options: [
      { value: NEXXEN_RATE_TYPE_CPM, label: NEXXEN_RATE_TYPE_CPM_LABEL },
      { value: NEXXEN_RATE_TYPE_PM, label: NEXXEN_RATE_TYPE_PM_LABEL },
      { value: NEXXEN_RATE_TYPE_FF, label: NEXXEN_RATE_TYPE_FF_LABEL },
      { value: NEXXEN_RATE_TYPE_CPMAP, label: NEXXEN_RATE_TYPE_CPMAP_LABEL },
    ],
  },
  {
    id: 'space',
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'textbox',
    id: 'currencyRate',
    column: 'currencyRate',
    label: NEXXEN_SEGMENT_FIELD_NAME.CURRENCY_RATE,
    regex: ENUM_REGEX.TWO_DECIMAL_PLACES,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    spacing: 'lg',
  },
  {
    type: 'datepicker',
    id: 'startDate',
    column: 'startDate',
    label: NEXXEN_SEGMENT_FIELD_NAME.START_DATE,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: new Date().toISOString().split('T')[0],
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    minDate: new Date().toISOString().split('T')[0],
    spacing: 'md',
  },
  {
    type: 'label',
    label: '',
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'datepicker',
    id: 'endDate',
    column: 'endDate',
    label: NEXXEN_SEGMENT_FIELD_NAME.END_DATE,
    variant: 'outlined',
    required: false,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    minDate: new Date().toISOString().split('T')[0],
    clearable: true,
    spacing: 'md',
  },
];

/**
 * Configuration for Nexxen Custom Segment fields.
 */
const nexxenCustomSegmentConfig = [
  {
    type: 'dropdown',
    id: 'availability',
    column: 'availability',
    label: NEXXEN_SEGMENT_FIELD_NAME.AVAILABILITY,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: NEXXEN_AVAILABILITY_SINGLE,
    disabled: false,
    isVisible: false,
    includeInPayload: true,
    InputLabelProps: { shrink: true },
    spacing: 'md',
    options: [
      { value: NEXXEN_AVAILABILITY_ALL, label: NEXXEN_AVAILABILITY_ALL_LABEL },
      {
        value: NEXXEN_AVAILABILITY_SINGLE,
        label: NEXXEN_AVAILABILITY_SINGLE_LABEL,
      },
    ],
  },
  {
    type: 'textbox',
    id: 'advertiserDestination',
    column: 'advertiserDestination',
    label: NEXXEN_SEGMENT_FIELD_NAME.ADVERTISER_ID,
    regex: ENUM_REGEX.NEXXEN_WHOLE_OR_MINUS_ONE,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
    spacing: 'md',
  },
  {
    type: 'label',
    label: '',
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'textbox',
    id: 'platformDestination',
    column: 'platformDestination',
    label: NEXXEN_SEGMENT_FIELD_NAME.MARKET_ID,
    regex: ENUM_REGEX.NEXXEN_WHOLE_OR_MINUS_ONE,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
    spacing: 'md',
  },
  {
    type: 'dropdown',
    id: 'rateType',
    column: 'rateType',
    label: NEXXEN_SEGMENT_FIELD_NAME.RATE_TYPE,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    spacing: 'lg',
    options: [
      { value: NEXXEN_RATE_TYPE_CPM, label: NEXXEN_RATE_TYPE_CPM_LABEL },
      { value: NEXXEN_RATE_TYPE_PM, label: NEXXEN_RATE_TYPE_PM_LABEL },
      { value: NEXXEN_RATE_TYPE_FF, label: NEXXEN_RATE_TYPE_FF_LABEL },
      { value: NEXXEN_RATE_TYPE_CPMAP, label: NEXXEN_RATE_TYPE_CPMAP_LABEL },
    ],
  },
  {
    type: 'label',
    label: '',
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'textbox',
    id: 'currencyRate',
    column: 'currencyRate',
    label: NEXXEN_SEGMENT_FIELD_NAME.CURRENCY_RATE,
    regex: ENUM_REGEX.TWO_DECIMAL_PLACES,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    spacing: 'lg',
  },
  {
    type: 'datepicker',
    id: 'startDate',
    column: 'startDate',
    label: NEXXEN_SEGMENT_FIELD_NAME.START_DATE,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: new Date().toISOString().split('T')[0],
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    minDate: new Date().toISOString().split('T')[0],
    spacing: 'md',
  },
  {
    type: 'label',
    label: '',
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'datepicker',
    id: 'endDate',
    column: 'endDate',
    label: NEXXEN_SEGMENT_FIELD_NAME.END_DATE,
    variant: 'outlined',
    required: false,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    minDate: new Date().toISOString().split('T')[0],
    clearable: true,
    spacing: 'md',
  },
];

/**
 * Fetches the form fields for a specific audience export type.
 *
 * @param exportType - The type of audience export (e.g., CUSTOM_SEGMENT, LIVERAMP_CUSTOM_SEGMENT).
 * @returns The array of form fields that are visible for the given export type.
 */
const getAudienceExportField = (exportType: string) => {
  switch (exportType) {
    case AUDIENCE_EXPORT_TYPES.OTS_SEGMENT:
    case AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE:
      return tradeDeskOTSSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT:
    case AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE:
      return tradeDeskCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT:
      return liveRampCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT:
    case AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE:
      return liveRampOtsSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT:
    case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE:
      return liveRampCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE:
      return metaCustomSegmentSeedAudienceConfig.filter(
        (field) => field.isVisible
      );

    case AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT:
      return metaCustomSegmentModeledAudienceConfig.filter(
        (field) => field.isVisible
      );

    case AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE:
      return tiktokCustomSegmentSeedAudienceConfig.filter(
        (field) => field.isVisible
      );

    case AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT:
      return yahooDspOTSSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT:
      return yahooDspCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT:
      return tiktokCustomSegmentModeledAudienceConfig.filter(
        (field) => field.isVisible
      );

    case AUDIENCE_EXPORT_TYPES.REDDIT_CUSTOM_SEGMENT:
      return redditCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT:
    case AUDIENCE_EXPORT_TYPES.AMAZON_OTS_SEGMENT:
      return amazonCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.APPSFLYER_CUSTOM_SEGMENT:
      return appsflyerCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT:
      return viantCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT:
      return nexxenOTSSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT:
      return nexxenCustomSegmentConfig.filter(
        (field) => field.isVisible || field.includeInPayload
      );

    default:
      return [];
  }
};

/**
 * Generates the audience export fields based on the provided export types.
 *
 * @param exportTypes - An array of export types to generate fields for.
 * @returns An array of objects containing the export type and its corresponding form fields.
 */
export const getAudienceExportFields = (exportTypes: string[] = []) => {
  const fields = [];

  for (const exportType of exportTypes) {
    fields.push({
      id: nanoid(), // Unique identifier for each field.
      exportType,
      formJSON: JSON.parse(JSON.stringify(getAudienceExportField(exportType))),
    });
  }

  return fields;
};
